import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';

import { downloadAttachment, uploadAttachmentContent } from './attachments-rest';
import {
  ARCHIVE_CLINICAL_ATTACHMENT_MUTATION,
  CLINICAL_ATTACHMENTS_QUERY,
  REGISTER_CLINICAL_ATTACHMENT_MUTATION,
  idempotencyContext,
} from './clinical.gql';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

const KINDS = [
  'lab_result_pdf',
  'xray_image',
  'ultrasound_image',
  'photo',
  'consent_form',
  'external_report',
  'other',
] as const;

const ATTACHMENT_STATUS_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
  pending_upload: 'muted',
  pending_review: 'warning',
  reviewed: 'success',
  archived: 'muted',
};

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function errCode(err: unknown): string {
  if (err instanceof ApolloError) return err.graphQLErrors[0]?.message ?? err.message;
  if (err instanceof Error) return err.message;
  return 'error';
}

export function AttachmentsSection({
  visitId,
  canWrite,
  writable,
}: {
  visitId: string;
  canWrite: boolean;
  writable: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_ATTACHMENTS_QUERY, {
    variables: { visit_id: visitId },
    skip: !visitId,
    fetchPolicy: 'cache-and-network',
  });
  const rows: any[] = data?.clinicalAttachments ?? [];

  const [register] = useMutation(REGISTER_CLINICAL_ATTACHMENT_MUTATION);
  const [archive] = useMutation(ARCHIVE_CLINICAL_ATTACHMENT_MUTATION);

  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<string>('lab_result_pdf');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Holds the registered (pending_upload) id so a failed REST upload is retried against
  // the SAME row instead of re-registering and leaving a trail of orphan rows.
  const [pendingId, setPendingId] = useState<string | null>(null);

  const onUpload = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      let id = pendingId;
      if (!id) {
        const reg = await register({
          variables: {
            input: {
              clinical_visit_id: visitId,
              kind,
              original_filename: file.name,
              content_type: file.type || 'application/octet-stream',
              title: title.trim() === '' ? undefined : title.trim(),
            },
          },
          context: idempotencyContext(),
        });
        id = reg.data?.registerClinicalAttachment?.id as string;
        setPendingId(id);
      }
      await uploadAttachmentContent(id, file);
      setFile(null);
      setTitle('');
      setPendingId(null);
      await refetch();
      showToast({ tone: 'success', title: t('clinical.toast_attachment_uploaded') });
    } catch (e) {
      const code = errCode(e);
      const known = ['clinical_attachment_too_large', 'clinical_visit_locked'];
      setError(known.includes(code) ? t(`clinical.error_${code}`) : code);
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async (att: any): Promise<void> => {
    try {
      await downloadAttachment(att.id, att.original_filename);
    } catch {
      setError(t('clinical.error_clinical_attachment_download_failed'));
    }
  };

  const onArchive = async (id: string): Promise<void> => {
    setError(null);
    try {
      await archive({ variables: { id }, context: idempotencyContext() });
      await refetch();
    } catch (e) {
      setError(errCode(e));
    }
  };

  return (
    <div className="space-y-3">
      {error ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
          {error}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('clinical.no_attachments')}</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {rows.map((a) => {
            const size = a.size_bytes != null ? Number(a.size_bytes) : null;
            const stored = a.stored_size_bytes != null ? Number(a.stored_size_bytes) : null;
            const saved = size != null && stored != null && stored < size;
            return (
              <div
                key={a.id}
                className={`space-y-2 rounded-md border p-3 ${a.status === 'archived' ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                    <FileText className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{a.title || a.original_filename}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline">{t(`clinical.attachment_kind_${a.kind}`)}</Badge>
                      <Badge variant={ATTACHMENT_STATUS_VARIANT[a.status] ?? 'muted'}>
                        {t(`clinical.attachment_status_${a.status}`)}
                      </Badge>
                    </div>
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">
                  {size != null ? humanSize(size) : '—'}
                  {saved ? ` → ${humanSize(stored as number)} (${a.compression})` : ''}
                </p>
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => void onDownload(a)}>
                    {t('clinical.download')}
                  </Button>
                  {canWrite && writable && a.status !== 'archived' ? (
                    <Button size="sm" variant="ghost" onClick={() => void onArchive(a.id)}>
                      {t('clinical.archive')}
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canWrite && writable ? (
        <div className="space-y-2 rounded-md border p-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>{t('clinical.attachment_kind')}</Label>
              <select
                className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value);
                  setPendingId(null);
                }}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`clinical.attachment_kind_${k}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t('clinical.attachment_title')}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} />
            </div>
          </div>
          <input
            type="file"
            className="text-sm"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPendingId(null);
            }}
            disabled={busy}
          />
          <p className="text-muted-foreground text-xs">{t('clinical.attachment_max_hint')}</p>
          <Button size="sm" onClick={() => void onUpload()} disabled={busy || !file}>
            {busy ? t('common.loading') : t('clinical.upload_attachment')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
